import React, { useState, useEffect } from "react";
import { Group, Box, Text, Menu, TextInput, Divider, useMantineTheme, ScrollArea } from "@mantine/core";
import { useDisclosure } from '@mantine/hooks';
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import tinycolor from "tinycolor2";

import { useDebounce } from "../../../hooks/useDebounce";
import { getFilterColors } from "../../../../utils/Filters";

export default function FilterOptionSortableItem({ id, name, color, onEdit, onDelete }) {
  const [opened, handlers] = useDisclosure(false);
  const [optionId, setOptionId] = useState(id);
  const [label, setLabel] = useState(typeof name === "string" ? name : "");
  const theme = useMantineTheme();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const _color = tinycolor(color).darken(60).toString();

  const FILTER_COLORS = getFilterColors(theme);

  useEffect(() => {
    if (id !== optionId) {
      setLabel(name);
      setOptionId(id);
    }
  }, [id, name, optionId]);

  const debouncedSave = useDebounce(({ value }) => {
    if (value.length === 0) return;
    onEdit(value);
  }, 1500);

  return (
    <Box ref={setNodeRef} style={style}>
      <Group
        mx={5}
        px={5}
        py={2}
        sx={{ cursor: "pointer", borderRadius: 5, "&:hover": { backgroundColor: "#f3f3f3" } }}
      >
        <Box {...listeners} {...attributes}>
          <FontAwesomeIcon icon={solid("grip-vertical")} size="xs" color="grey" />
        </Box>
        <Menu
          opened={opened}
          onOpen={handlers.open}
          onClose={handlers.close}
          zIndex={9999999}
          closeOnItemClick={false}
          style={{ flex: 1 }}
          control={
            <Group noWrap spacing={0}>
              <Text
                size="xs"
                color={_color || "grey"}
                py={2}
                px={5}
                style={{ backgroundColor: color || "#f3f3f3", borderRadius: 5 }}
                lineClamp={1}
              >
                {name}
              </Text>
              <Box ml={10} sx={{ flex: 1 }} />
              <FontAwesomeIcon icon={solid("chevron-right")} size="xs" color="grey" />
            </Group>
          }
        >
          <TextInput
            value={label}
            size="xs"
            m={5}
            sx={{ flex: 1 }}
            onChange={(e) => {
              setLabel(e.currentTarget.value);
              debouncedSave({ value: e.currentTarget.value });
            }}
          />
          <Menu.Item
            p={5}
            onClick={() => {
              handlers.close();
              if (typeof onDelete === "function") {
                onDelete();
              }
            }}
          >
            <Group spacing={0}>
              <FontAwesomeIcon icon="trash" color="grey" width={20} />
              <Text size="sm" color="grey" pl={10}>Delete</Text>
            </Group>
          </Menu.Item>

          <Divider my={10} />

          <ScrollArea style={{ height: 200 }}>
            {FILTER_COLORS.map((item) => (
              <Menu.Item
                key={item.value}
                rightSection={item.value === color ? <FontAwesomeIcon icon={solid("check")} color="grey" width={20} /> : undefined}
                p={5}
                onClick={() => {
                  onEdit(name, item.value);
                  handlers.close();
                }}
              >
                <Group spacing={0}>
                  <Box sx={{ width: 16, height: 16, borderRadius: 5, backgroundColor: item.value }} />
                  <Text size="xs" color="grey" pl={10}>{item.name}</Text>
                </Group>
              </Menu.Item>  
            ))}
          </ScrollArea>
        </Menu>
      </Group>
    </Box>
  );
};